/**
 * Message List Component - Enhanced with Modern Design
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Fade,
  Slide,
  Zoom,
  keyframes,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BuildIcon from '@mui/icons-material/Build';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import ImageViewer from './ImageViewer';
import { format } from 'date-fns';

import useChatStore from '../store/chatStore';
import useAuthStore from '../store/authStore';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// Typing indicator animation
const TypingIndicator = () => {
  const bounce = keyframes`
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-10px); }
  `;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#90a4ae',
            animation: `${bounce} 1.4s infinite ease-in-out`,
            animationDelay: `${i * 0.16}s`,
          }}
        />
      ))}
    </Box>
  );
};

// AI Thinking indicator with streaming status
const AIThinking = ({ streamingStatus }) => {
  const { t } = useTranslation(['common', 'chat']);
  const glow = keyframes`
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.1); }
  `;

  const pulse = keyframes`
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.8; }
  `;

  // Get phase-specific icon and color
  const getPhaseConfig = () => {
    switch (streamingStatus?.phase) {
      case 'tool_calling':
        return { icon: <BuildIcon />, color: '#ff9800', bgGradient: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)' };
      case 'tool_executing':
        return { icon: <BuildIcon />, color: '#4caf50', bgGradient: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' };
      case 'responding':
        return { icon: <EditNoteIcon />, color: '#2196f3', bgGradient: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' };
      case 'thinking':
      default:
        return { icon: <PsychologyIcon />, color: '#667eea', bgGradient: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' };
    }
  };

  const { icon, color, bgGradient } = getPhaseConfig();

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          mt: 2,
          mb: 2,
          justifyContent: 'flex-start',
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            width: 40,
            height: 40,
            animation: `${glow} 2s ease-in-out infinite`,
          }}
        >
          <SmartToyIcon />
        </Avatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Main thinking indicator */}
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 3,
              background: bgGradient,
              minWidth: 100,
              transition: 'all 0.3s ease',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: color, display: 'flex', alignItems: 'center' }}>
                {icon}
              </Box>
              <Typography variant="body2" sx={{ color: '#546e7a', fontWeight: 500 }}>
                {t('common:status.aiThinking')}
              </Typography>
              <TypingIndicator />
            </Box>
          </Paper>

          {/* Status message below - smaller and faded */}
          {streamingStatus?.statusMessage && (
            <Fade in timeout={300}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  pl: 1.5,
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 12, color: color, opacity: 0.6 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    opacity: 0.65,
                    fontStyle: 'italic',
                    letterSpacing: '0.3px',
                  }}
                >
                  {streamingStatus.statusMessage}
                </Typography>
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </Fade>
  );
};

export default function MessageList() {
  const { t } = useTranslation(['common', 'chat']);
  const { user } = useAuthStore();
  const { messages, isStreaming, currentSession, streamingStatus } = useChatStore();
  const messagesEndRef = useRef(null);

  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ src: '', alt: '' });

  const handleImageClick = (src, alt) => {
    setSelectedImage({ src, alt: alt || 'Image' });
    setImageViewerOpen(true);
  };

  const handleImageViewerClose = () => {
    setImageViewerOpen(false);
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  }, [messages, isStreaming]);

  if (!currentSession && messages.length === 0) {
    const roleKey =
      user?.role === 'MASTER'
        ? 'master'
        : user?.role === 'ADMIN' || user?.department === 'ALL'
          ? 'admin'
          : user?.department === 'HR'
            ? 'hr'
            : user?.department === 'SALES'
              ? 'sales'
              : user?.department === 'GUEST' || user?.role === 'GUEST'
                ? 'guest'
                : 'user';

    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
        }}
      >
        <Zoom in timeout={800}>
          <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                mb: 3,
              }}
            >
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                }}
              >
                <SmartToyIcon sx={{ fontSize: 60 }} />
              </Avatar>
              <AutoAwesomeIcon
                sx={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                  color: '#ffd700',
                  fontSize: 30,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                }}
              />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('chat:welcome.title')}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {t('chat:welcome.subtitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('chat:welcome.instruction')}
            </Typography>
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 3,
                bgcolor:
                  user?.department === 'ALL' || user?.role === 'ADMIN' || user?.role === 'MASTER'
                    ? '#f3e5f5'
                    : user?.department === 'HR'
                      ? '#e3f2fd'
                      : user?.department === 'SALES'
                        ? '#e8f5e9'
                        : user?.department === 'GUEST' || user?.role === 'GUEST'
                          ? '#fff3e0'
                          : '#f5f5f5',
                border: '2px solid',
                borderColor:
                  user?.department === 'ALL' || user?.role === 'ADMIN' || user?.role === 'MASTER'
                    ? '#7c4dff'
                    : user?.department === 'HR'
                      ? '#1976d2'
                      : user?.department === 'SALES'
                        ? '#2e7d32'
                        : user?.department === 'GUEST' || user?.role === 'GUEST'
                          ? '#f57c00'
                          : '#757575',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('common:status.loggedInAs')}{' '}
                <strong>{t(`common:status.access.${roleKey}`)}</strong>
              </Typography>
            </Paper>
          </Box>
        </Zoom>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        p: 3,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '10px',
          '&:hover': {
            background: '#555',
          },
        },
      }}
    >
      {messages.map((message, index) => (
        <MessageBubble
          key={message.id || index}
          message={message}
          user={user}
          index={index}
          t={t}
          onImageClick={handleImageClick}
        />
      ))}

      {isStreaming && <AIThinking streamingStatus={streamingStatus} />}

      <div ref={messagesEndRef} />

      {/* Image Viewer Modal */}
      <ImageViewer
        open={imageViewerOpen}
        onClose={handleImageViewerClose}
        src={selectedImage.src}
        alt={selectedImage.alt}
      />
    </Box>
  );
}

function MessageBubble({ message, user, index, t, onImageClick }) {
  const isUser = message.role === 'user';
  const departmentColor = user?.department === 'HR' ? '#1976d2' : '#2e7d32';
  const departmentGradient = user?.department === 'HR'
    ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
    : 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)';

  // Handle authenticated file download
  const handleDownload = async (url, filename) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Vui lòng đăng nhập lại để tải file.');
        return;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      // Create blob link to download
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Không thể tải file. Vui lòng thử lại.');
    }
  };

  // Custom renderer for ReactMarkdown
  const markdownComponents = {
    // Handle img tags (from HTML or markdown)
    img: ({ node, src, alt, ...props }) => {
      return (
        <Box
          sx={{
            my: 2,
            position: 'relative',
            display: 'inline-block',
            maxWidth: '100%',
            cursor: 'pointer',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
            },
            '&:hover .zoom-overlay': {
              opacity: 1,
            },
          }}
          onClick={() => onImageClick(src, alt)}
        >
          <Box
            component="img"
            src={src}
            alt={alt || 'Image'}
            sx={{
              maxWidth: '100%',
              maxHeight: 400,
              display: 'block',
              borderRadius: 2,
              objectFit: 'contain',
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
            }}
          />
          <Box
            className="zoom-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.4)',
              opacity: 0,
              transition: 'opacity 0.3s ease',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.6)',
                px: 2,
                py: 1,
                borderRadius: 5,
              }}
            >
              <ZoomInIcon fontSize="small" />
              <Typography variant="caption">Click to zoom</Typography>
            </Box>
          </Box>
          {alt && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                color: 'text.secondary',
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              {alt}
            </Typography>
          )}
        </Box>
      );
    },
    // Handle links
    a: ({ node, href, children, ...props }) => {
      // Check if it's a download link pattern
      const downloadMatch = href?.match(/^http:\/\/localhost:8001\/chat\/download\/([^\/]+)\/(.+)$/);

      if (downloadMatch) {
        const [, agentName, filename] = downloadMatch;
        return (
          <Box
            component="a"
            href="#"
            className="download-link"
            onClick={(e) => {
              e.preventDefault();
              handleDownload(href, filename);
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              marginTop: '12px',
              marginBottom: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white !important',
              textDecoration: 'none !important',
              borderRadius: '12px',
              fontWeight: 600,
              fontSize: '0.95em',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-3px) scale(1.02)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
              },
              '&:active': {
                transform: 'translateY(-1px) scale(0.98)',
              },
            }}
          >
            <FileDownloadIcon sx={{ fontSize: 20 }} />
            Tải xuống file
            <InsertDriveFileIcon sx={{ fontSize: 18 }} />
          </Box>
        );
      }

      // Regular link
      return <a href={href} {...props}>{children}</a>;
    },
  };

  return (
    <Slide direction={isUser ? 'left' : 'right'} in timeout={300 + index * 50}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 3,
          animation: 'fadeInUp 0.3s ease-out',
          '@keyframes fadeInUp': {
            from: {
              opacity: 0,
              transform: 'translateY(20px)',
            },
            to: {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isUser ? 'row-reverse' : 'row',
            maxWidth: '75%',
            gap: 1.5,
          }}
        >
          {/* Avatar with modern design */}
          <Avatar
            sx={{
              background: isUser ? departmentGradient : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              width: 40,
              height: 40,
              boxShadow: isUser
                ? `0 4px 14px ${departmentColor}40`
                : '0 4px 14px rgba(102, 126, 234, 0.3)',
              border: '2px solid white',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            {isUser ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
          </Avatar>

          {/* Message Content with glassmorphism */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              background: isUser
                ? departmentGradient
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              color: isUser ? 'white' : 'text.primary',
              borderRadius: 3,
              boxShadow: isUser
                ? `0 4px 20px ${departmentColor}30`
                : '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: isUser ? 'none' : '1px solid rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: isUser
                  ? `0 6px 24px ${departmentColor}40`
                  : '0 6px 24px rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            <Typography
              variant="caption"
              display="block"
              sx={{
                opacity: isUser ? 0.9 : 0.7,
                mb: 1,
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
              }}
            >
              {isUser ? t('common:message.you') : t('common:message.assistant')}
            </Typography>

            <Box
              sx={{
                '& p': {
                  margin: 0,
                  lineHeight: 1.6,
                },
                '& a.download-link': {
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  marginTop: '12px',
                  marginBottom: '8px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '12px',
                  fontWeight: 600,
                  fontSize: '0.95em',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transition: 'left 0.5s',
                  },
                  '&:hover': {
                    transform: 'translateY(-3px) scale(1.02)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                    '&::before': {
                      left: '100%',
                    },
                  },
                  '&:active': {
                    transform: 'translateY(-1px) scale(0.98)',
                  },
                },
                '& strong': {
                  fontWeight: 700,
                },
                '& code': {
                  bgcolor: isUser ? 'rgba(0,0,0,0.15)' : 'rgba(102, 126, 234, 0.1)',
                  color: isUser ? 'white' : '#667eea',
                  padding: '3px 6px',
                  borderRadius: 1.5,
                  fontFamily: '"Fira Code", "Courier New", monospace',
                  fontSize: '0.9em',
                  fontWeight: 500,
                },
                '& pre': {
                  bgcolor: isUser ? 'rgba(0,0,0,0.15)' : 'rgba(102, 126, 234, 0.08)',
                  padding: 2,
                  borderRadius: 2,
                  overflow: 'auto',
                  border: isUser ? 'none' : '1px solid rgba(102, 126, 234, 0.2)',
                  '& code': {
                    bgcolor: 'transparent',
                    padding: 0,
                  },
                },
                '& ul, & ol': {
                  marginLeft: 2,
                  marginTop: 1,
                  marginBottom: 1,
                },
                '& li': {
                  marginBottom: 0.5,
                },
              }}
            >
              {/* Render markdown with custom components (supports HTML img tags) */}
              <ReactMarkdown
                components={markdownComponents}
                rehypePlugins={[rehypeRaw]}
              >
                {message.content}
              </ReactMarkdown>
            </Box>

            {message.created_at && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                  mt: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    opacity: isUser ? 0.8 : 0.6,
                    fontSize: '0.7rem',
                  }}
                >
                  {format(new Date(message.created_at), 'HH:mm')}
                </Typography>
                {isUser && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    ✓
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Slide>
  );
}
